import type { ComputeUsingConfig, TableCalcDirection } from '../query_builder/visualSpec'
import { cleanBracketedFieldRef, normalizeTableauFieldKey, normalizeTableauFieldToken } from './fieldIdentity'

export type TableauTableCalcAttrs = Record<string, string>

export type TableCalcDim = {
  field: string
  sql?: string | null
  /**
   * Optional override SQL expression used only when this dim appears in a
   * window `PARTITION BY` clause. Lets calculated dimensions (e.g.
   * `[haircutcount] > 0`) partition by their underlying compiled formula
   * instead of the hoisted display alias, so the partition survives
   * downstream layers that re-project the base extract without carrying
   * the derived alias forward.
   */
  partitionSql?: string | null
  shelf?: 'rows' | 'columns' | 'marks' | 'other'
}

export type ComputeUsingResult = {
  config: ComputeUsingConfig
  warnings: string[]
  source: 'attrs' | 'default'
}

const norm = (s: any) => normalizeTableauFieldKey(String(s ?? '')) || String(s ?? '').trim().toLowerCase()

const normalizeComputeUsing = (raw: string): string => {
  const s = String(raw ?? '').trim().toLowerCase()
  if (!s) return ''
  return s.replace(/\s+/g, ' ')
}

const hasAnyAttrKey = (attrs: TableauTableCalcAttrs, keys: string[]): boolean =>
  keys.some((k) => {
    const key = String(k || '').trim()
    return key ? Boolean(String(attrs[key] ?? '').trim()) : false
  })

const toBracketList = (names: string[]): string =>
  names
    .map((n) => String(n ?? '').trim())
    .filter(Boolean)
    .map((n) => `[${n.replaceAll(']', ']]')}]`)
    .join(', ')

export function normalizeTableauTableCalcAttrs(raw: any): TableauTableCalcAttrs {
  if (!raw) return {}
  if (Array.isArray(raw)) {
    const first = raw.find((x) => x && typeof x === 'object') as any
    return first ? normalizeTableauTableCalcAttrs(first) : {}
  }
  if (typeof raw === 'object') {
    const out: TableauTableCalcAttrs = {}
    const obj = raw as any
    const attrsObj = (obj && typeof obj.attrs === 'object') ? (obj.attrs as any) : null

    const source = attrsObj || obj
    for (const [k, v] of Object.entries(source)) {
      const kk = String(k || '').trim()
      if (!kk) continue
      const vv = String(v ?? '').trim()
      if (!vv) continue
      out[kk] = vv
    }

    // Promote typed canonical fields into attrs when not already present.
    const computeUsing = String(obj?.compute_using ?? obj?.computeUsing ?? '').trim()
    if (computeUsing && !hasAnyAttrKey(out, ['compute-using', 'compute_using'])) {
      out['compute-using'] = computeUsing
    }
    const orderingType = String(obj?.ordering_type ?? obj?.orderingType ?? '').trim()
    if (orderingType && !hasAnyAttrKey(out, ['ordering-type', 'ordering_type'])) {
      out['ordering-type'] = orderingType
    }
    const orderingField = String(obj?.ordering_field ?? obj?.orderingField ?? '').trim()
    if (orderingField && !hasAnyAttrKey(out, ['ordering-field', 'ordering_field'])) {
      out['ordering-field'] = orderingField
    }

    const addressingArr = Array.isArray(obj?.addressing)
      ? (obj.addressing as any[]).map((v) => String(v ?? '').trim()).filter(Boolean)
      : []
    if (addressingArr.length && !hasAnyAttrKey(out, ['addressing', 'compute-using-addressing', 'compute_using_addressing'])) {
      out.addressing = toBracketList(addressingArr)
    }
    const partitioningArr = Array.isArray(obj?.partitioning)
      ? (obj.partitioning as any[]).map((v) => String(v ?? '').trim()).filter(Boolean)
      : []
    if (partitioningArr.length && !hasAnyAttrKey(out, ['partitioning', 'compute-using-partitioning', 'compute_using_partitioning'])) {
      out.partitioning = toBracketList(partitioningArr)
    }

    const atLevel = String(obj?.at_level ?? obj?.atLevel ?? '').trim()
    if (
      atLevel
      && !hasAnyAttrKey(out, ['at-the-level', 'at_the_level', 'at-level', 'at_level', 'at-the-level-field'])
    ) {
      out['at-the-level'] = atLevel
    }
    const restartEvery = String(obj?.restart_every ?? obj?.restartEvery ?? '').trim()
    if (restartEvery && !hasAnyAttrKey(out, ['restart-level', 'restart_level', 'restart'])) {
      out['restart-level'] = restartEvery
    }
    return out
  }
  return {}
}

const splitOuterInner = (dims: TableCalcDim[]) => {
  if (dims.length <= 1) return { outer: [] as TableCalcDim[], inner: dims }
  return { outer: dims.slice(0, dims.length - 1), inner: dims.slice(dims.length - 1) }
}

const reverse = <T,>(arr: T[]): T[] => [...arr].reverse()

const dedupeDims = (dims: TableCalcDim[]) => {
  const out: TableCalcDim[] = []
  const seen = new Set<string>()
  for (const d of dims) {
    const k = norm(d.field)
    if (!k || seen.has(k)) continue
    seen.add(k)
    out.push(d)
  }
  return out
}

const parseDimList = (raw: string): string[] => {
  const s = String(raw ?? '').trim()
  if (!s) return []
  const bracketed = s.match(/\[[^\]]+\](?:\.\[[^\]]+\])?/g) || []
  if (bracketed.length) {
    return bracketed
      .map((x) => cleanBracketedFieldRef(x))
      .map((x) => String(x ?? '').trim())
      .filter(Boolean)
  }
  return s.split(',').map((x) => String(x ?? '').trim()).filter(Boolean)
}

const stripOuterFieldDelimiters = (raw: string): string => {
  let s = String(raw ?? '').trim()
  if (!s) return ''
  if ((s.startsWith('[') && s.endsWith(']')) || (s.startsWith('"') && s.endsWith('"'))) {
    s = s.slice(1, -1).trim()
  }
  return s
}

const lastQualifiedFieldSegment = (raw: string): string => {
  const s = String(raw ?? '').trim()
  if (!s || !s.includes('.')) return ''
  const seg = s.split('.').map(stripOuterFieldDelimiters).filter(Boolean).pop()
  return String(seg ?? '').trim()
}

const matchKeysForDimRef = (raw: string): string[] => {
  const out: string[] = []
  const seen = new Set<string>()
  const push = (value: string) => {
    const s = String(value ?? '').trim()
    if (!s || seen.has(s)) return
    seen.add(s)
    out.push(s)
  }

  const s = String(raw ?? '').trim()
  if (!s) return out

  const cleaned = cleanBracketedFieldRef(s)
  const normalized = normalizeTableauFieldToken(cleaned)
  const lastSeg = lastQualifiedFieldSegment(s)
  const normalizedLastSeg = normalizeTableauFieldToken(lastSeg)

  push(s)
  push(cleaned)
  push(normalized)
  push(lastSeg)
  push(normalizedLastSeg)

  return out
}

const bestEffortOrderingFieldRef = (raw: string): string => {
  const s = String(raw ?? '').trim()
  if (!s) return ''
  if (s.startsWith('sql:')) return s
  const lastSeg = lastQualifiedFieldSegment(s)
  if (lastSeg) return lastSeg
  const cleaned = cleanBracketedFieldRef(s)
  const normalized = normalizeTableauFieldToken(cleaned)
  const best = String(normalized || cleaned || s).trim()
  if (best && !/^null$/i.test(best)) return best
  const fallback = s
    .split('.')
    .map(stripOuterFieldDelimiters)
    .filter(Boolean)
    .pop()
  return String(fallback ?? '').trim() || s
}

const matchDims = (names: string[], dims: TableCalcDim[]): TableCalcDim[] => {
  if (!names.length) return []
  const byKey = new Map<string, TableCalcDim>()
  for (const d of dims) {
    const k = norm(d.field)
    if (k && !byKey.has(k)) byKey.set(k, d)
  }
  const out: TableCalcDim[] = []
  for (const n of names) {
    let hit: TableCalcDim | null = null
    for (const candidate of matchKeysForDimRef(n)) {
      const key = norm(candidate)
      hit = (key && byKey.get(key)) || null
      if (hit) break
    }
    if (hit) out.push(hit)
  }
  return out
}

const dimsMinus = (all: TableCalcDim[], minus: TableCalcDim[]): TableCalcDim[] => {
  const drop = new Set(minus.map((d) => norm(d.field)).filter(Boolean))
  return all.filter((d) => {
    const k = norm(d.field)
    return k ? !drop.has(k) : true
  })
}

const stripSqlPrefix = (raw: string): string => {
  const s = String(raw ?? '').trim()
  return s.startsWith('sql:') ? s.slice(4).trim() : s
}

const containsWindowCall = (raw: string): boolean => {
  const sql = stripSqlPrefix(raw)
  return /\bover\s*(?:\(|[A-Za-z_])/i.test(sql)
}

const toKey = (d: TableCalcDim, useSql?: boolean): string => {
  const sql = String(d.sql ?? '').trim()
  if (useSql && sql && !containsWindowCall(sql)) return `sql:${sql}`
  return d.field
}

const toPartitionKey = (d: TableCalcDim, useSql?: boolean): string => {
  const partitionSql = String(d.partitionSql ?? '').trim()
  if (useSql && partitionSql && !containsWindowCall(partitionSql)) {
    return `sql:${partitionSql}`
  }
  return toKey(d, useSql)
}

const toOrderKey = (rawExpr: string, fallbackField: string, useSql?: boolean): string => {
  const raw = String(rawExpr ?? '').trim()
  if (raw) {
    if (useSql && !containsWindowCall(raw)) {
      return raw.startsWith('sql:') ? raw : `sql:${raw}`
    }
    if (fallbackField) return fallbackField
    return raw
  }
  return fallbackField
}

const firstAttrValue = (attrs: TableauTableCalcAttrs, keys: string[]): string | null => {
  for (const k of keys) {
    const v = String(attrs[k] ?? '').trim()
    if (v) return v
  }
  return null
}

const parseAtLevel = (attrs: TableauTableCalcAttrs): string | null => {
  const keys = [
    'at-the-level',
    'at_the_level',
    'at-level',
    'at_level',
    'at-the-level-field',
  ]
  return firstAttrValue(attrs, keys)
}

const parseRestartLevel = (attrs: TableauTableCalcAttrs): string | null => {
  return firstAttrValue(attrs, [
    'restart-level',
    'restart_level',
    'restart',
  ])
}

export function deriveComputeUsingFromTableauAttrs(opts: {
  attrs?: TableauTableCalcAttrs | null
  rowDims: TableCalcDim[]
  colDims: TableCalcDim[]
  otherDims?: TableCalcDim[]
  useSql?: boolean
  directionForField?: (dim: TableCalcDim) => TableCalcDirection
  orderExprForField?: (dim: TableCalcDim) => string | null
  directionForFieldRef?: (fieldRefRaw: string) => TableCalcDirection
  orderExprForFieldRef?: (fieldRefRaw: string) => string | null
}): ComputeUsingResult {
  const warnings: string[] = []
  const attrs = normalizeTableauTableCalcAttrs(opts.attrs || {})
  const rowDims = opts.rowDims || []
  const colDims = opts.colDims || []
  const otherDims = opts.otherDims || []
  const allDims = dedupeDims([...rowDims, ...colDims, ...otherDims])

  const computeUsing = normalizeComputeUsing(String(attrs['compute-using'] || attrs.compute_using || ''))
  const orderingType = String(attrs['ordering-type'] || attrs.ordering_type || '').trim()
  const orderingFieldRaw = String(attrs['ordering-field'] || attrs.ordering_field || '').trim()
  const addressingRaw = String(attrs['addressing'] || attrs['compute-using-addressing'] || '').trim()
  const partitioningRaw = String(attrs['partitioning'] || attrs['compute-using-partitioning'] || '').trim()
  const atLevelRaw = parseAtLevel(attrs)
  const restartLevelRaw = parseRestartLevel(attrs)

  const addrList = parseDimList(addressingRaw)
  const partList = parseDimList(partitioningRaw)
  let addressing = matchDims(addrList, allDims)
  let partitioning = matchDims(partList, allDims)

  const rowInner = splitOuterInner(rowDims).inner
  const rowOuter = splitOuterInner(rowDims).outer
  const colInner = splitOuterInner(colDims).inner
  const colOuter = splitOuterInner(colDims).outer

  const addrsFromPreset = (preset: string): TableCalcDim[] => {
    const colsInner = reverse(colInner)
    const rowsInner = reverse(rowInner)
    const colsAll = reverse(colDims)
    const rowsAll = reverse(rowDims)
    if (preset === 'table(across)') return colsAll
    if (preset === 'table(down)') return rowsAll
    if (preset === 'table(across then down)') return [...colsAll, ...rowsAll]
    if (preset === 'table(down then across)') return [...rowsAll, ...colsAll]
    if (preset === 'pane(across)') return colsInner
    if (preset === 'pane(down)') return rowsInner
    if (preset === 'pane(across then down)') return [...colsInner, ...rowsInner]
    if (preset === 'pane(down then across)') return [...rowsInner, ...colsInner]
    if (preset === 'cell') return []
    return []
  }

  if (addrList.length || partList.length) {
    if (addressing.length === 0 && addrList.length) {
      warnings.push('Specific-dim addressing did not match view dims; falling back to defaults.')
      addressing = []
    }
    if (partitioning.length === 0 && partList.length) {
      warnings.push('Specific-dim partitioning did not match view dims; falling back to defaults.')
      partitioning = []
    }
    if (addressing.length && !partitioning.length) {
      partitioning = dimsMinus(allDims, addressing)
    } else if (partitioning.length && !addressing.length) {
      addressing = dimsMinus(allDims, partitioning)
    }
  } else if (computeUsing) {
    addressing = addrsFromPreset(computeUsing)
    if (computeUsing.startsWith('table(')) {
      if (computeUsing.includes('across')) {
        partitioning = dedupeDims([...rowDims, ...otherDims])
      } else if (computeUsing.includes('down')) {
        partitioning = dedupeDims([...colDims, ...otherDims])
      } else {
        partitioning = otherDims
      }
    } else if (computeUsing.startsWith('pane(')) {
      if (computeUsing.includes('across')) {
        partitioning = dedupeDims([...rowDims, ...colOuter, ...otherDims])
      } else if (computeUsing.includes('down')) {
        partitioning = dedupeDims([...colDims, ...rowOuter, ...otherDims])
      } else {
        partitioning = dedupeDims([...rowOuter, ...colOuter, ...otherDims])
      }
    } else if (computeUsing === 'cell') {
      partitioning = allDims
    } else {
      partitioning = dimsMinus(allDims, addressing)
    }
  } else if (orderingType) {
    const ot = orderingType.toLowerCase()
    // Tableau `ordering-type` in imported workbook metadata is effectively opposite
    // the visual axis label for these copied/table-calc measures:
    // - Rows    => compute across columns (address column dims; partition by row dims)
    // - Columns => compute down rows (address row dims; partition by column dims)
    // If one side is empty, gracefully fall back to the non-empty side.
    if (ot === 'rows') addressing = reverse(colDims.length ? colDims : rowDims)
    else if (ot === 'columns') addressing = reverse(rowDims.length ? rowDims : colDims)
    else if (ot === 'pane') addressing = reverse([...colDims, ...rowDims])
    else if (ot === 'table') addressing = reverse([...colDims, ...rowDims])
    else if (ot === 'field' && orderingFieldRaw) addressing = matchDims([orderingFieldRaw], allDims)
    partitioning = dimsMinus(allDims, addressing)
  } else {
    addressing = reverse([...colDims, ...rowDims])
    partitioning = otherDims
  }

  if (atLevelRaw) {
    const levelHit = matchDims([atLevelRaw], addressing)
    if (levelHit.length) {
      const idx = addressing.findIndex((d) => norm(d.field) === norm(levelHit[0]!.field))
      if (idx >= 0) {
        const extraPart = addressing.slice(0, idx + 1)
        partitioning = dedupeDims([...partitioning, ...extraPart])
      }
    } else {
      warnings.push(`At-the-level "${atLevelRaw}" did not match view dims.`)
    }
  }

  if (restartLevelRaw) {
    const restartHit = matchDims([restartLevelRaw], addressing)
    if (restartHit.length) {
      partitioning = dedupeDims([...partitioning, ...restartHit])
    } else {
      warnings.push(`Restarting-every "${restartLevelRaw}" did not match addressing dims.`)
    }
  }

  addressing = dedupeDims(addressing)
  partitioning = dedupeDims(partitioning)

  const order_by = (() => {
    const orderings: Array<{ field: string; direction: TableCalcDirection }> = []
    const push = (d: TableCalcDim) => {
      const rawExpr = opts.orderExprForField?.(d) || ''
      const field = rawExpr
        ? toOrderKey(rawExpr, d.field, opts.useSql)
        : toKey(d, opts.useSql)
      if (!field) return
      orderings.push({ field, direction: opts.directionForField?.(d) ?? 'Asc' })
    }
    if (orderingType && orderingType.toLowerCase() === 'field' && orderingFieldRaw) {
      const hit = matchDims([orderingFieldRaw], allDims)
      if (hit.length) {
        push(hit[0]!)
        return orderings
      }
      const rawExpr = String(opts.orderExprForFieldRef?.(orderingFieldRaw) || '').trim()
      const field = rawExpr
        ? toOrderKey(rawExpr, bestEffortOrderingFieldRef(orderingFieldRaw), opts.useSql)
        : bestEffortOrderingFieldRef(orderingFieldRaw)
      if (field) {
        orderings.push({
          field,
          direction: opts.directionForFieldRef?.(orderingFieldRaw) ?? 'Asc',
        })
      }
      return orderings
    }
    for (const d of addressing) push(d)
    return orderings
  })()

  return {
    config: {
      addressing: addressing.map((d) => toKey(d, opts.useSql)),
      partitioning: partitioning.map((d) => toPartitionKey(d, opts.useSql)),
      order_by,
      frame: null,
    },
    warnings,
    source: (addrList.length || partList.length || computeUsing || orderingType || atLevelRaw || restartLevelRaw) ? 'attrs' : 'default',
  }
}

